import React, { useContext, useEffect, useRef, useState } from "react";
import { generatePath, Navigate, useNavigate, useParams } from "react-router-dom";
import track from "react-tracking";

import { Flex } from "@chakra-ui/react";

import { createPassword } from "api/auth/auth";
import { activate, IValidateActivationTokenResponse, validateActivationToken } from "api/trips/trips";
import { acceptMarketingConsent } from "api/user/user";
import ctripActivationImage from "assets/images/ctrip-activation@1x.jpg";
import { TwoColumnsImageLayout } from "components/Layouts";
import RegistrationView, { ActivationSubmitData } from "components/Trips/RegistrationView/RegistrationView";
import { PAGE_PATHS } from "config/routes";
import { SessionContext } from "contexts";
import { Loader } from "elements";

type PageParams = {
  uuid: string;
  token: string;
};

const TripRegistrationPage = () => {
  const [validationData, setValidationData] = useState<IValidateActivationTokenResponse | undefined>(undefined);
  const [isTokenValidated, setIsTokenValidated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingError, setIsLoadingError] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitError, setIsSubmitError] = useState(false);
  const [redirectToActivation, setRedirectToActivation] = useState(false);

  const navigate = useNavigate();

  const { uuid, token } = useParams<PageParams>();

  const { user, refreshSession } = useContext(SessionContext);

  const activateTrip = async (
    submitData: ActivationSubmitData,
    refreshToken: string,
    tripUuid: string,
    token: string
  ) => {
    const { password, marketingConsent } = submitData;
    const fail = () => {
      setIsSubmitError(true);
      setIsSubmitting(false);
    };

    setIsSubmitting(true);

    const [createPasswordError] = await createPassword({
      password: password,
      token: refreshToken,
    });

    if (createPasswordError) {
      fail();
      return;
    }

    if (marketingConsent) {
      const [acceptMarketingConsentError] = await acceptMarketingConsent();

      if (acceptMarketingConsentError) {
        fail();
        return;
      }
    }

    const [activateError] = await activate(tripUuid, token);

    if (activateError) {
      fail();
      return;
    } else {
      refreshSession();
      setRedirectToActivation(true);
    }

    setIsSubmitting(false);
  };

  const validateToken = async (tripUuid: string, token: string) => {
    setIsLoading(true);

    const [error, response] = await validateActivationToken(tripUuid, token);

    if (error) {
      setIsLoadingError(true);
    } else {
      setValidationData(response as IValidateActivationTokenResponse);
    }

    setIsTokenValidated(true);
    setIsLoading(false);
  };

  // makes an api call to validate the token coming from URL
  const tokenValidatedRef = useRef(false);
  useEffect(() => {
    if (!isTokenValidated && !tokenValidatedRef.current && uuid && token) {
      tokenValidatedRef.current = true;

      setIsTokenValidated(true);

      validateToken(uuid, token);
    }
  }, [isTokenValidated, uuid, token]);

  // if the token is validated and user activation status is not inactive, redirect to home page
  useEffect(() => {
    if (isTokenValidated && validationData) {
      if (validationData.activationStatus !== "inactive") {
        navigate(generatePath(PAGE_PATHS.TRIPS.ACTIVATE_PAGE, { token: uuid }));
      }
    }
  }, [isTokenValidated, validationData]);

  // redirects user to default trip activation flow after successful account registration
  useEffect(() => {
    if (redirectToActivation && user) {
      navigate(generatePath(PAGE_PATHS.TRIPS.ACTIVATE_PAGE, { token: uuid }));
    }
  }, [redirectToActivation, user, uuid]);

  if (isLoading) {
    return (
      <Flex alignItems="center" justifyContent="center">
        <Loader />
      </Flex>
    );
  }

  if (isLoadingError) {
    return <Navigate to={generatePath(PAGE_PATHS.HOME_PAGE)} />;
  }

  return (
    <TwoColumnsImageLayout imageSrc={ctripActivationImage}>
      <RegistrationView
        isSubmitting={isSubmitting}
        isSubmitError={isSubmitError}
        email={validationData?.email || ""}
        onSubmit={(submitData) => {
          activateTrip(submitData, validationData?.resetPasswordToken || "", uuid || "", token || "");
        }}
      />
    </TwoColumnsImageLayout>
  );
};

export default track({ page_type: "TripRegistration" })(TripRegistrationPage);
